// src/views/kiddo-games/recognize2/KiddoRecognizeView2.tsx
// Rozpoznaj tłumaczenie Easy — kopia src/views/recognize2/RecognizeView2.tsx, rozwijana osobno.
// Różnice: obrazek słowa nad ramką z polskim napisem (jak w Powtórz), własne style, następna gra z grupy Easy,
// osobne zapamiętanie przełącznika polskiego audio.
/* eslint-disable max-len */
import React, {
  FC, memo, useState, useEffect, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import ReactCardFlip from 'react-card-flip';
import { shuffle } from 'lodash';
import classes from './KiddoRecognizeView2.module.less';
import FixWidthContent from '../../../components/FixWidthContent';
import withTagManager from '../../../hooks/withTagManager';
import { WordFromList } from './WordFromList';
// Kopie ikon z recognize/ dla Easy — obwódka z kreski, żeby styl mógł ją pocienić jak na ekranie końcowym
import { ReactComponent as Repeat } from '../../../assets/icons/games/kiddo/refresh-ccw-rec.svg';
import { ReactComponent as Correct } from '../../../assets/icons/games/kiddo/check-circle-2.svg';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import { BASE_API_URL } from '../../../services/ApiService';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../../constants/Roles';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import { getListWordId } from '../../../utils/gameWords';
import WordCounter from '../../../components/WordCounter';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import { FitText } from '../../../components/FitText/FitText';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

export const KiddoRecognizeView2: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList;

  const [currentWords, setCurrentWords] = useState(shuffle(words));
  const [wordOnScreen, setWordOnScreen] = useState(0);
  const [listEndInfo, setListEndInfo] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.RECOGNIZE2_EASY);

  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'kiddo_recognize2_playPolish' });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const goToNext = useGoToRoute(Path.KIDDO_DRAGLETTERS);

  const repeatGame = () => {
    stopAudio();
    stopPolishAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCurrentWords(shuffle(words));
    setListEndInfo(false);
    setWordOnScreen(0);
    setIsFlipped(false);
  };

  useEffect(() => {
    if (!listEndInfo && currentWords[wordOnScreen]) {
      const { definition } = currentWords[wordOnScreen];
      playPolishAudio(definition);
    }

    return () => {
      stopPolishAudio();
    };
  }, [wordOnScreen, listEndInfo, currentWords, playPolishAudio, stopPolishAudio]);

  const handleFlip = (withSound: boolean = false) => {
    // 1. ZAWSZE i NATYCHMIAST przełączamy stan wizualny
    setIsFlipped((current) => !current);

    if (withSound) {
      stopPolishAudio();

      const { term } = currentWords[wordOnScreen];

      const cleanTerm = cleanTextForTTS(term);
      if (!cleanTerm) return;

      const englishUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;

      playAudio(englishUrl).catch(() => {});
    }
  };

  const handleNextClick = (): void => {
    // CORRECT to samoocena ucznia — gra nie zna wyniku. Tak samo REPEAT niżej; „?” (podgląd tłumaczenia) to nie próba.
    // Drugie kliknięcie przed przejściem do następnego słowa (czekający timeoutRef) to ta sama próba
    if (!timeoutRef.current) {
      recordAnswer(null, currentWords[wordOnScreen]?.term);
    }

    stopAudio();
    stopPolishAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen < currentWords.length - 1) {
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
      }, 100);
    } else {
      setListEndInfo(true);
    }
  };

  const handleRepeat = (): void => {
    const wordToRepeat = {
      ...currentWords[wordOnScreen],
      id: `${currentWords[wordOnScreen].id}_repeat_${Date.now()}`,
      listWordId: getListWordId(currentWords[wordOnScreen]),
    };

    if (!timeoutRef.current) {
      recordAnswer(null, wordToRepeat.term);
    }

    stopAudio();
    stopPolishAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen === currentWords.length - 1) {
      // Przed powtórką wchodzi inne słowo — pierwsze z listy, ale nie to z błędem (mogło stać na początku
      // i wrócić na koniec z wcześniejszego REPEAT). Gdy innego nie ma (wszystkie słowa zapisane tak samo), słowo wraca od razu
      const filler = currentWords.find(({ term }) => term !== wordToRepeat.term);
      const updatedCurrentWords = filler ? [...currentWords, filler, wordToRepeat] : [...currentWords, wordToRepeat];
      setCurrentWords(updatedCurrentWords);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
      }, 100);
    } else {
      setCurrentWords((prev) => [...prev, wordToRepeat]);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
      }, 100);
    }
  };

  useEffect(() => () => {
    stopAudio();
    stopPolishAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [stopAudio, stopPolishAudio]);

  const current = currentWords[wordOnScreen];

  return (
    <>
      <FixWidthContent className={classes.main}>
        {!listEndInfo && (
        <WordCounter
          current={wordOnScreen + 1}
          total={currentWords.length}
        />
        )}
        {/* Na telefonie trzymanym poziomo obrazek, karta ze słowem i karta „?” stoją w jednym rzędzie
            (KiddoRecognizeView2.module.less) */}
        {!listEndInfo && (
          <div className={classes.stage}>
            <div className={classes.wordWithPicture}>
              <WordFromList
                id={current.id}
                content={current.definition}
                image={current.image}
              />
            </div>

            <div className={classes.flipper}>
              <ReactCardFlip
                isFlipped={isFlipped}
                flipDirection="vertical"
                flipSpeedFrontToBack={0.15}
                flipSpeedBackToFront={0.15}
              >
                <div
                  className={classes.front}
                  onClick={() => handleFlip(true)}
                  aria-hidden="true"
                >
                  ?
                </div>
                {/* Długi zwrot dostaje mniejszą czcionkę, żeby zmieścił się w karcie (na telefonie) */}
                <FitText
                  className={classes.back}
                  onClick={() => handleFlip(false)}
                  aria-hidden="true"
                >
                  {current.term}
                </FitText>
              </ReactCardFlip>
            </div>

            <div className={classes.options}>
              <Button
                onClick={handleRepeat}
                className={wordOnScreen === currentWords.length - 1 ? `${classes.optionButton} ${classes.lastWord}` : classes.optionButton}
              >
                <Repeat className={classes.btnLabel} />
                <br />
                {t('games:buttons.repeat')}
              </Button>

              <Button
                onClick={handleNextClick}
                className={classes.optionButton}
              >
                <Correct className={classes.btnLabel} />
                <br />
                {t('games:buttons.correct')}
              </Button>
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} kiddo />
        )}

        {!listEndInfo && <UnsplashCredits words={current ? [current] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && (
            <EditTermButton
              wordId={getListWordId(current)}
              currentTerm={current.term}
              currentDefinition={current.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(current);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(KiddoRecognizeView2);
